import { query, collection, onSnapshot, setDoc, updateDoc, deleteDoc, doc, where, getDoc } from "@firebase/firestore"
import type { Unsubscribe } from "firebase/firestore"
import { authWorker, db } from "../config/firebase-config"
import type { IFolder } from "../models/Folder"
import type { IUser } from "../models/User"
import type AppStore from "../stores/AppStore"
import type AppApi from "./AppApi"
import { isDev } from "../config/env"
import { logger } from "../functions/Logger"
import { createUserWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth"

export default class UserApi {
  path: string | null = null

  constructor(
    private api: AppApi,
    private store: AppStore,
  ) {}

  getPath() {
    return "users"
  }

  async getAll() {
    // get the db path
    const path = this.getPath()
    if (!path) return

    // remove all items from store
    this.store.user.removeAll()

    // create the query
    const $query = query(collection(db, path))

    // new promise
    return await new Promise<Unsubscribe>((resolve, reject) => {
      // on snapshot
      const unsubscribe = onSnapshot(
        $query,
        // onNext
        (querySnapshot) => {
          const items: IUser[] = []
          querySnapshot.forEach((doc) => {
            const user = { uid: doc.id, ...doc.data() } as IUser
            if (isDev) items.push(user)
            else if (!user.devUser) items.push(user)
          })
          this.store.user.load(items)
          resolve(unsubscribe)
        },
        // onError
        (error) => {
          reject(error)
          logger.error("Error getting all users", error)
        },
      )
    })
  }

  async getAllSubordinates(uid: string) {
    // get the db path
    const path = this.getPath()
    if (!path) return

    // remove all items from store
    this.store.user.removeAll()

    // create the query
    const $query = query(collection(db, path), where("supervisor", "==", uid))

    // new promise
    return await new Promise<Unsubscribe>((resolve, reject) => {
      // on snapshot
      const unsubscribe = onSnapshot(
        $query,
        // onNext
        (querySnapshot) => {
          const items: IUser[] = []
          querySnapshot.forEach((doc) => {
            items.push({ uid: doc.id, ...doc.data() } as IUser)
          })
          this.store.user.load(items)
          resolve(unsubscribe)
        },
        // onError
        (error) => {
          reject(error)
          logger.error("Error getting all subordinates", error)
        },
      )
    })
  }

  async getByDepartment(departmentId: string) {
    const path = this.getPath()
    if (!path) return

    this.store.user.removeAll()

    const $query = query(collection(db, path), where("department", "==", departmentId))

    return await new Promise<Unsubscribe>((resolve, reject) => {
      const unsubscribe = onSnapshot(
        $query,
        (querySnapshot) => {
          const items: IUser[] = []
          querySnapshot.forEach((doc) => {
            items.push({ uid: doc.id, ...doc.data() } as IUser)
          })
          this.store.user.load(items)
          resolve(unsubscribe)
        },
        (error) => {
          reject(error)
          logger.error("Error getting all users by department", error)
        },
      )
    })
  }

  async getByDivision(divisionId: string) {
    const path = this.getPath()
    if (!path) return

    this.store.user.removeAll()

    const $query = query(collection(db, path), where("division", "==", divisionId))

    return await new Promise<Unsubscribe>((resolve, reject) => {
      const unsubscribe = onSnapshot(
        $query,
        (querySnapshot) => {
          const items: IUser[] = []
          querySnapshot.forEach((doc) => {
            items.push({ uid: doc.id, ...doc.data() } as IUser)
          })
          this.store.user.load(items)
          resolve(unsubscribe)
        },
        (error) => {
          reject(error)
          logger.error("Error getting all users by division", error)
        },
      )
    })
  }

  async getByUid(id: string) {
    const path = this.getPath()
    if (!path) return

    const unsubscribe = onSnapshot(doc(db, path, id), (doc) => {
      if (!doc.exists()) return
      const item = { uid: doc.id, ...doc.data() } as IUser
      this.store.user.load([item])
    })

    return unsubscribe
  }

  // Create user with Firebase Auth and Firestore (for admin creating new users)
  async create(user: IUser) {
    const path = this.getPath()
    if (!path) return

    const password = "demo@123"
    const { email } = user

    // Type guard to exit early if email is null
    if (!email) {
      logger.error("Cannot create user without a valid email")
      throw new Error("Email is required to create a user")
    }

    try {
      logger.info("Creating new user with Firebase Auth", { email })

      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(authWorker, email, password)

      if (userCredential) {
        // Set the UID from Firebase Auth
        user.uid = userCredential.user.uid

        // Create user document in Firestore "users" collection
        await setDoc(doc(db, path, user.uid), user)

        // Load user into store
        this.store.user.load([user])

        // Send password reset email so user can set their own password
        try {
          await sendPasswordResetEmail(authWorker, email)
          logger.info("Password reset email sent successfully", { email })
        } catch (emailError) {
          logger.error("Failed to send password reset email", emailError)
          // Don't fail the entire process if email fails
        }

        // Create user folder (only for admin-created users)
        await this.createOrUpdateUserFolder(user)

        logger.info("User created successfully in users collection", { uid: user.uid, email })
        return user
      }

      throw new Error("Failed to create user credential")
    } catch (error) {
      logger.error("Error creating user", error)
      throw error
    }
  }

  // update item
  async update(item: IUser) {
    const path = this.getPath()
    if (!path) return

    // update in db
    try {
      await updateDoc(doc(db, path, item.uid), {
        ...item,
      })
      // update in store
      this.store.user.load([item])
      this.createOrUpdateUserFolder(item) // create folder
      logger.info("User updated successfully", { uid: item.uid })
    } catch (error) {
      logger.error("Error updating user:", error)
      throw error
    }
  }

  async updateDateRange(uid: string, startDate: string, endDate: string, scorecardId: string) {
    const path = this.getPath()
    if (!path) return

    // Create a reference to the document
    const docRef = doc(db, path, uid)

    // Get the current user data
    const docSnap = await getDoc(docRef)
    if (!docSnap.exists()) {
      logger.error("User document not found", { uid })
      throw new Error("User not found")
    }

    try {
      const newData = { ...docSnap.data() }

      // Ensure scorecardPeriod exists and is an array
      if (!Array.isArray(newData.scorecardPeriod)) {
        newData.scorecardPeriod = []
      }

      // Find the index of the existing scorecardId
      const existingIndex = newData.scorecardPeriod.findIndex((item: any) => item.scorecardId === scorecardId)

      if (existingIndex !== -1) {
        // If scorecardId exists, override the existing entry
        newData.scorecardPeriod[existingIndex] = { startDate, endDate, scorecardId }
      } else {
        // If scorecardId doesn't exist, push the new entry
        newData.scorecardPeriod.push({ startDate, endDate, scorecardId })
      }

      // Update document in the database with the latest data
      await updateDoc(docRef, newData)
      logger.info("User date range updated successfully", { uid, scorecardId })
    } catch (error) {
      logger.error("Error updating date range:", error)
      throw error
    }
  }

  // delete user
  async delete(item: IUser) {
    const path = this.getPath()
    if (!path) return

    // remove from db
    try {
      await deleteDoc(doc(db, path, item.uid))
      // remove from store
      this.store.user.remove(item.uid) // Remove from memory
      logger.info("User deleted successfully", { uid: item.uid })
    } catch (error) {
      logger.error("Error deleting user:", error)
      throw error
    }
  }

  // create/update user root folder in drive
  private async createOrUpdateUserFolder(item: IUser) {
    const userFolder: IFolder = {
      id: item.uid,
      name: item.displayName || `${item.firstName} ${item.lastName}` || "Unknown User",
      type: "User",
      department: item.department,
      parentId: item.department,
      path: ["root", item.department],
      createdBy: "auto",
      createdAt: Date.now(),
      supervisor: item.supervisor,
    }

    // create in db
    try {
      await this.api.folder.autoCreateFolder(userFolder)
      logger.info("User folder created/updated successfully", { uid: item.uid })
    } catch (error) {
      logger.error("Error creating user folder:", error)
      // Don't throw - folder creation failure shouldn't prevent user operations
    }
  }
}
